import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface LossDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stockItems: any[];
  onConfirm: (quantity: number, selectedProductId: string, explanation: string, proofUrl?: string) => void;
}

export const LossDialog = ({
  open,
  onOpenChange,
  stockItems,
  onConfirm,
}: LossDialogProps) => {
  const [quantity, setQuantity] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [explanation, setExplanation] = useState("");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedProductId) {
      toast.error("Veuillez sélectionner un produit");
      return;
    }

    const parsedQuantity = parseInt(quantity);
    if (!parsedQuantity || parsedQuantity <= 0) {
      toast.error("Veuillez entrer une quantité valide");
      return;
    }

    if (!explanation.trim()) {
      toast.error("Veuillez expliquer la raison de la perte");
      return;
    }

    const selectedProduct = stockItems.find(item => item.id === selectedProductId);
    if (!selectedProduct) {
      toast.error("Produit non trouvé");
      return;
    }

    if (parsedQuantity > selectedProduct.quantity) {
      toast.error("La quantité ne peut pas être supérieure au stock disponible");
      return;
    }

    try {
      setIsUploading(true);
      let proofUrl = null;

      if (proofFile) {
        const fileExt = proofFile.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('loss_proofs')
          .upload(filePath, proofFile);

        if (uploadError) {
          throw uploadError;
        }

        proofUrl = filePath;
      }

      onConfirm(parsedQuantity, selectedProductId, explanation, proofUrl);
      setQuantity("");
      setSelectedProductId("");
      setExplanation("");
      setProofFile(null);
    } catch (error: any) {
      console.error("Erreur lors de l'upload:", error);
      toast.error("Erreur lors de l'upload du fichier");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Le fichier est trop volumineux (max 5MB)");
        return;
      }
      setProofFile(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Déclarer une perte</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="product">Produit</Label>
            <select
              id="product"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Sélectionnez un produit</option>
              {stockItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.data.name} - Stock: {item.quantity}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="quantity">Quantité perdue</Label>
            <Input
              id="quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
              max={stockItems.find(item => item.id === selectedProductId)?.quantity}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="explanation">Explication de la perte</Label>
            <Textarea
              id="explanation"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Expliquez la raison de la perte..."
              className="min-h-[100px]"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="proof">Preuve de perte (optionnel)</Label>
            <Input
              id="proof"
              type="file"
              onChange={handleFileChange}
              accept="image/*,video/*"
              className="cursor-pointer"
            />
            <p className="text-sm text-muted-foreground">
              Formats acceptés: images et vidéos (max 5MB)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? "Envoi en cours..." : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};